import { useState } from "react";

import { Trans } from "@lingui/react/macro";
import { cn } from "@pontimed/ui";
import { AnimatePresence, m } from "framer-motion";
import { ChevronDown, Eye, Lock, Server, ShieldCheck } from "lucide-react";

import { useLegalSheet } from "@/components/legal/legal-sheet-provider";

type Variant = "compact" | "full";
type Audience = "patient" | "provider";

interface DataSecurityBannerProps {
  /** "compact" = one-line, "full" = card with features */
  variant?: Variant;
  /** "patient" = personal health, "provider" = patient data protection */
  audience?: Audience;
  className?: string;
}

const FEATURE_ICONS = [Lock, Server, Eye, ShieldCheck] as const;

/**
 * 1:1 port of legacy `src/components/account/data-security-banner.tsx`.
 *
 * "How we protect it" opens the in-portal LegalSheet on the `security`
 * segment — same shell as the other legal entries (footer, side-nav). Keeps
 * the user inside the app on PWA / Capacitor, no external browser hop.
 */
export function DataSecurityBanner({
  variant = "compact",
  audience = "patient",
  className,
}: DataSecurityBannerProps) {
  const { open: openLegal } = useLegalSheet();

  const [open, setOpen] = useState(false);

  if (variant === "compact") {
    return (
      <div className={cn("bg-muted/50 flex items-center gap-2.5 rounded-xl px-4 py-3", className)}>
        <ShieldCheck className="text-primary size-4 shrink-0" />
        <p className="text-muted-foreground text-xs leading-relaxed">
          <span className="text-foreground font-medium">
            {audience === "provider" ? (
              <Trans>For us, data security is as important as your health and your patients'</Trans>
            ) : (
              <Trans>For us, data security is as important as your health is for you</Trans>
            )}
          </span>
          {/* Facts only — the same set as the footer trust badges
              (_docs/legal/certification-plan-nis2-ehds.md §5). */}
          {" · "}
          <Trans>Encrypted medical data</Trans>
          {" · "}
          <Trans>Data hosted in the EU</Trans>
          {" · "}
          <Trans>Passwordless sign-in</Trans>
          {" · "}
          <button
            type="button"
            onClick={() => openLegal("security")}
            className="text-primary hover:underline"
          >
            <Trans>Details</Trans>
          </button>
        </p>
      </div>
    );
  }

  // Full variant — collapsible
  const features = [
    <Trans key="f1">Medical data encrypted, with a separate key for each patient</Trans>,
    <Trans key="f2">Database hosted in the EU (Azure West Europe — Amsterdam)</Trans>,
    <Trans key="f3">You control who sees your data</Trans>,
    // Facts only — no NIS2/EHDS compliance claim
    // (_docs/legal/certification-plan-nis2-ehds.md §5).
    <Trans key="f4">Passwordless sign-in, with passkeys and two-factor authentication</Trans>,
  ];

  return (
    <div className={cn("border-border bg-card overflow-hidden rounded-xl border", className)}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="bg-primary/5 active:bg-primary/10 flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors"
      >
        <div className="bg-primary/10 flex size-8 items-center justify-center rounded-full">
          <ShieldCheck className="text-primary size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-foreground text-sm font-semibold">
            {audience === "provider" ? (
              <Trans>For us, data security is as important as your health and your patients'</Trans>
            ) : (
              <Trans>For us, data security is as important as your health is for you</Trans>
            )}
          </p>
          <p className="text-muted-foreground text-xs">
            {audience === "provider" ? (
              <Trans>Your patients' data is encrypted and the database is hosted in the EU.</Trans>
            ) : (
              <Trans>Your data is encrypted and the database is hosted in the EU.</Trans>
            )}
          </p>
        </div>
        <ChevronDown
          className={cn(
            "text-muted-foreground size-4 shrink-0 transition-transform duration-200",
            open && "rotate-180",
          )}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <m.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="grid gap-0 divide-y border-t sm:grid-cols-2 sm:divide-x sm:divide-y-0">
              {features.map((node, i) => {
                const Icon = FEATURE_ICONS[i];
                return (
                  <div key={i} className="flex items-start gap-2.5 px-5 py-3">
                    <Icon className="text-primary mt-0.5 size-3.5 shrink-0" />
                    <p className="text-muted-foreground text-xs leading-relaxed">{node}</p>
                  </div>
                );
              })}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}
